import { faBell, faExclamationCircle } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button } from '@quero-delivery/quero-components-web';
import { useEffect } from 'react';
import { Form } from '@radix-ui/react-form';
import type { SubmitHandler } from 'react-hook-form';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { colors } from '@/Themes/Colors';
import style from './active-post-paid-modal.module.scss';
import { usePutPostPaid } from '@/_features/places/api/put-post-paid';
import { queryClient } from '@/_config/lib/react-query/query-client';
import { GET_PLACE_ID } from '@/_features/places/api/constants';
import { TextInput } from '@/_components/text-input/text-input';
import type { PostPaidTypesForm } from '../../entities/post-paid.types';
import { postPaidLimitSchema } from '../../entities/post-paid.constants';
import {
  convertMoneyInputFormatToFloat,
  convertStringToFloatWithTwoDecimals,
  formatCurrency,
} from '@/Utils/FnUtils';
import { Modal } from '@/_components/modal/modal';
import { useGetPlaceById } from '@/_features/places/api/get-place-by-id';

interface ActivePostPaidModalProps {
  open: boolean;
  onClose: () => void;
  placeId: string;
}

export const ActivePostPaidModal = ({
  open,
  onClose,
  placeId,
}: ActivePostPaidModalProps) => {
  const { mutate: updatePostPaid, isLoading } = usePutPostPaid();

  const { data: place } = useGetPlaceById(
    {
      placeId: placeId || '',
    },
    {
      enabled: open && !!placeId,
    },
  );

  const minLimitAllowed = place?.minLimitAllowed || 0;
  const maxLimitAllowed = place?.maxLimitAllowed || 0;

  const { control, handleSubmit, clearErrors } = useForm<PostPaidTypesForm>({
    resolver: yupResolver(postPaidLimitSchema),
    context: {
      minAllowed: minLimitAllowed,
      maxAllowed: maxLimitAllowed,
    },
  });

  const minLimitAllowedFormatted = formatCurrency(minLimitAllowed);
  const maxLimitAllowedFormatted = formatCurrency(maxLimitAllowed);

  useEffect(() => {
    if (open) {
      clearErrors();
    }
  }, [open]);

  const handleSendRequest: SubmitHandler<PostPaidTypesForm> = data => {
    updatePostPaid(
      {
        placeId,
        statusPostPaid: 'ACTIVE',
        postPaidLimit: Number(
          convertStringToFloatWithTwoDecimals(data?.postPaidLimit),
        ),
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries([GET_PLACE_ID, { placeId }]);
          onClose();
        },
      },
    );
  };

  return (
    <Modal
      bodyClassName={style.bodyContainer}
      onClose={onClose}
      open={open}
      width={525}
      title="Bank - Ativação do pós pago"
    >
      <>
        <FontAwesomeIcon icon={faBell} color={colors.gray60} size="4x" />
        <span className={style.title}>
          Você está adicionando o pós pago a este parceiro.
        </span>
        <span className={style.description}>
          {`Você está ativando o modelo pós-pago para o parceiro ${place?.nomeExibicao}. Por favor, defina um limite de crédito mensal para ele.`}
        </span>
        <Form className={style.form}>
          <Controller
            control={control}
            name="postPaidLimit"
            render={({ field: { value, onChange }, fieldState: { error } }) => {
              return (
                <>
                  <TextInput
                    value={value}
                    label="Limite de Crédito Mensal:"
                    labelClassName={style.inputLabel}
                    onChange={e => {
                      onChange(convertMoneyInputFormatToFloat(e.target.value));
                    }}
                    name="limit"
                    error={!!error?.message}
                    tag="R$"
                  />

                  <div className={style.creditsLimit}>
                    <span>{minLimitAllowedFormatted}</span>
                    <span>{maxLimitAllowedFormatted}</span>
                  </div>

                  {!!error?.message && (
                    <div className={style.errorMessage}>
                      <FontAwesomeIcon
                        icon={faExclamationCircle}
                        color={colors.danger50}
                      />
                      {error.message}
                    </div>
                  )}
                </>
              );
            }}
          />
        </Form>
        <div className={style.buttons}>
          <Button
            className={style.leftButton}
            onClick={onClose}
            disabled={isLoading}
            secondary
          >
            cancelar
          </Button>
          <Button
            className={style.rightButton}
            onClick={handleSubmit(handleSendRequest)}
            disabled={isLoading}
          >
            ativar o pós e salvar limite
          </Button>
        </div>
      </>
    </Modal>
  );
};
